/**
 * AI output provenance — the JSONB payload stored in
 * `audit_log.ai_provenance` (migration 150) and surfaced in the
 * audit-search UI and SIEM forwarder.
 *
 * Closes the "AI output provenance / labelling" compliance row.
 * Regulated buyers want to be able to ask:
 *
 *   1. "Was this artifact written by a model or a human?"
 *   2. "Which provider + model produced it, and when?"
 *   3. "Is the model output identifiable for audit / discovery?"
 *
 * Every Rynora audit-log row that captures an AI call now carries
 * this payload. The same shape is forwarded verbatim to customer
 * SIEMs by the mig 149 webhook, so a downstream SOC can rebuild a
 * model-usage report without a Rynora console session.
 *
 * Wire compatibility: this is a versioned schema. Future versions
 * add fields; they do not rename or drop existing ones. SIEM
 * consumers should ignore unknown fields and treat all fields
 * except `version`, `provider`, `model`, `feature` as optional.
 */
import { z } from 'zod';

/**
 * AI features that emit provenance. Loosely typed to allow new
 * surfaces to land without a contract change, but the canonical
 * values are listed so docs + filters can render a stable set.
 */
export const AiProvenanceFeatureSchema = z
  .string()
  .min(1)
  .max(64)
  .describe(
    'Canonical values: intent, intent-batch, intent-suggest, intent-explain, intent-rca, vision, tool-use, script-generate, flake-narrative, release-summary, tag-rerank, observability-fix.',
  );
export type AiProvenanceFeature = z.infer<typeof AiProvenanceFeatureSchema>;

/**
 * Which key paid for the call. Identical semantics to
 * `LlmCallLog.byokScope` so the two surfaces are pivotable. Kept
 * as a literal-string union (rather than importing
 * `LlmByokScopeSchema`) so this module can be consumed by
 * downstream packages that don't depend on the telemetry contracts.
 */
export const AiProvenanceByokScopeSchema = z.enum(['user', 'org', 'platform']).nullable();

export const AiProvenanceSchema = z.object({
  /** Schema version. Bump when we change field semantics. */
  version: z.literal(1),

  /** Provider id — see `IntentProvider` in @rynora/intent-engine. */
  provider: z.string().min(1).max(64),

  /** Model identifier as sent on the wire. */
  model: z.string().min(1).max(200),

  /** Which Rynora surface called the model. */
  feature: AiProvenanceFeatureSchema,

  /**
   * Provider-side generation id when known. Used to cross-reference
   * provider dashboards during incident retros. May be null when the
   * provider doesn't return one.
   */
  generationId: z.string().max(200).nullable().optional(),

  /** Usage numbers (best-effort; null when provider doesn't report). */
  inputTokens: z.number().int().min(0).nullable().optional(),
  outputTokens: z.number().int().min(0).nullable().optional(),
  latencyMs: z.number().int().min(0).nullable().optional(),

  /** Which key paid for this call. */
  byokScope: AiProvenanceByokScopeSchema.optional(),

  /**
   * SHA-256 of the prompt (hex, 64 chars). Lets a reviewer prove
   * "this exact prompt was sent to this exact model" without
   * persisting the prompt itself. Optional — some surfaces (e.g.
   * tool-use) chain multiple prompts and elide it.
   */
  promptHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'promptHash must be a lowercase hex sha-256')
    .nullable()
    .optional(),

  /**
   * Wall-clock when the call completed. Redundant with the audit-log
   * row's `last_at`, but SIEM consumers that only ingest the JSONB
   * blob would otherwise have no timestamp.
   */
  createdAt: z.string().datetime(),
});
export type AiProvenance = z.infer<typeof AiProvenanceSchema>;

/**
 * Helper: build a provenance payload with sensible defaults. Used
 * by every AI audit-log writer in the API package.
 */
export function makeAiProvenance(
  input: Omit<AiProvenance, 'version' | 'createdAt'> & { createdAt?: string },
): AiProvenance {
  return {
    version: 1,
    createdAt: input.createdAt ?? new Date().toISOString(),
    provider: input.provider,
    model: input.model,
    feature: input.feature,
    generationId: input.generationId ?? null,
    inputTokens: input.inputTokens ?? null,
    outputTokens: input.outputTokens ?? null,
    latencyMs: input.latencyMs ?? null,
    byokScope: input.byokScope ?? null,
    promptHash: input.promptHash ?? null,
  };
}
